/**
 * Conversation attachments + extraction provenance — SPA-side types.
 *
 * Mirrors vnext-api/vnext/schemas/attachment.py. Wire field names stay
 * snake_case at the boundary; transformers convert to camelCase for in-app
 * use. This module imports nothing app-side so it can be a shared leaf
 * (report-request.ts and extraction.ts both depend on `ProvenanceRef`).
 */

/** Source of a conversation attachment row. */
export type ConversationAttachmentSource = 'analyst_upload' | 'inbound_email' | 'zip_extracted';

/** Who provided the file. Client files auto-flow (RR attach + Backoffice
 *  dispatch); internal files are shared explicitly. */
export type AttachmentClassification = 'client' | 'internal';

/** The page an upload came from. The client conversation is one thread
 *  per case, so the thread alone no longer says which request a client's
 *  file is about — the page does. */
export interface ConversationUploadContext {
  entityType: 'enquiry' | 'report' | 'report_request';
  entityId: string;
}

export interface ConversationAttachment {
  id: string;
  chatThreadId: string;
  /** Set once the file is bound to a message; null for inbound-email /
   *  pending uploads not yet referenced by a sent message. */
  chatMessageId: string | null;
  /** For zip members, the id of the zip they were unpacked from. */
  parentAttachmentId: string | null;
  source: ConversationAttachmentSource | string;
  classification: AttachmentClassification;
  filename: string;
  contentType: string | null;
  sizeBytes: number;
  sha256: string | null;
  freshdeskAttachmentId: number | null;
  createdByUserId: string | null;
  createdAt: string | null;
  /** Presigned, time-limited GET URL the backend embeds in responses. May
   *  go stale — refresh via getConversationAttachmentDownloadUrl. */
  downloadUrl: string | null;
}

/** Where an extracted candidate / RR was derived from. At least one of
 *  chatMessageId / conversationAttachmentId is set. */
export interface ProvenanceRef {
  chat_message_id?: string | null;
  conversation_attachment_id?: string | null;
  source_hint?: string | null;
}

/** Per-file digest state for the triage rail — null for containers
 *  (zips) that never digest. */
export type AttachmentDigestStatus = 'pending' | 'completed' | 'failed';

export interface ReportRequestAttachment {
  id: string;
  reportRequestId: string;
  conversationAttachmentId: string;
  createdByUserId: string | null;
  createdAt: string | null;
  filename: string | null;
  contentType: string | null;
  sizeBytes: number | null;
  downloadUrl: string | null;
  classification: AttachmentClassification | null;
  digestStatus: AttachmentDigestStatus | null;
  digestError: string | null;
}

/** Read-only provenance row for an RR's /sources endpoint. */
export interface ReportRequestSource {
  id: string;
  reportRequestId: string;
  chatMessageId: string | null;
  conversationAttachmentId: string | null;
  sourceHint: string | null;
  createdAt: string | null;
  filename: string | null;
}

// --- API (snake_case) mirrors ---

export interface ApiConversationAttachment {
  id: string;
  chat_thread_id: string;
  chat_message_id: string | null;
  parent_attachment_id: string | null;
  source: string;
  classification: AttachmentClassification;
  filename: string;
  content_type: string | null;
  size_bytes: number;
  sha256: string | null;
  freshdesk_attachment_id: number | null;
  created_by_user_id: string | null;
  created_at: string | null;
  download_url: string | null;
}

export interface ApiReportRequestAttachment {
  id: string;
  report_request_id: string;
  conversation_attachment_id: string;
  created_by_user_id: string | null;
  created_at: string | null;
  filename: string | null;
  content_type: string | null;
  size_bytes: number | null;
  download_url: string | null;
  classification?: AttachmentClassification | null;
  digest_status?: AttachmentDigestStatus | null;
  digest_error?: string | null;
}

export interface ApiReportRequestSource {
  id: string;
  report_request_id: string;
  chat_message_id: string | null;
  conversation_attachment_id: string | null;
  source_hint: string | null;
  created_at: string | null;
  filename: string | null;
}

export function transformToConversationAttachment(
  api: ApiConversationAttachment
): ConversationAttachment {
  return {
    id: api.id,
    chatThreadId: api.chat_thread_id,
    chatMessageId: api.chat_message_id,
    parentAttachmentId: api.parent_attachment_id,
    source: api.source,
    classification: api.classification,
    filename: api.filename,
    contentType: api.content_type,
    sizeBytes: api.size_bytes,
    sha256: api.sha256,
    freshdeskAttachmentId: api.freshdesk_attachment_id,
    createdByUserId: api.created_by_user_id,
    createdAt: api.created_at,
    downloadUrl: api.download_url,
  };
}

export function transformToReportRequestAttachment(
  api: ApiReportRequestAttachment
): ReportRequestAttachment {
  return {
    id: api.id,
    reportRequestId: api.report_request_id,
    conversationAttachmentId: api.conversation_attachment_id,
    createdByUserId: api.created_by_user_id,
    createdAt: api.created_at,
    filename: api.filename,
    contentType: api.content_type,
    sizeBytes: api.size_bytes,
    downloadUrl: api.download_url,
    classification: api.classification ?? null,
    digestStatus: api.digest_status ?? null,
    digestError: api.digest_error ?? null,
  };
}

export function transformToReportRequestSource(api: ApiReportRequestSource): ReportRequestSource {
  return {
    id: api.id,
    reportRequestId: api.report_request_id,
    chatMessageId: api.chat_message_id,
    conversationAttachmentId: api.conversation_attachment_id,
    sourceHint: api.source_hint,
    createdAt: api.created_at,
    filename: api.filename,
  };
}

/** Single source of truth for the uploader's accept string. Locked
 *  filetypes: zip, XLSX, DOCX, PDF, CSV, PNG, JPEG, BMP, text. */
export const CONVERSATION_ATTACHMENT_ACCEPT =
  '.zip,.xlsx,.docx,.pdf,.csv,.png,.jpg,.jpeg,.bmp,.txt';

/** MIME list for @mantine/dropzone `accept`. */
export const CONVERSATION_ATTACHMENT_MIME = [
  'application/zip',
  'application/x-zip-compressed',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // xlsx
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // docx
  'application/pdf',
  'text/csv',
  'image/png',
  'image/jpeg',
  'image/bmp',
  'text/plain',
];

export function formatFileSize(bytes: number): string {
  if (bytes < 1024) {
    return `${bytes} B`;
  }
  if (bytes < 1024 * 1024) {
    return `${(bytes / 1024).toFixed(1)} KB`;
  }
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
